import { QueryClient, useQuery } from '@tanstack/react-query';
import { type ReactNode, Suspense, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  getAppId,
  getAppName,
  getAppVersion,
  getCurrentUserDetails,
} from '@dynatrace-sdk/app-environment';
import { sendIntent } from '@dynatrace-sdk/navigation';
import {
  DescriptionIcon,
  ExternalLinkIcon,
  FeedbackIcon,
  GuideIcon,
  HelpIcon,
  InformationIcon,
  KeyboardIcon,
  PlayIcon,
  WhatsNewIcon,
} from '@dynatrace/strato-icons';

import { Button } from '../../buttons/button/Button.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import type { WithChildren } from '../../core/types/with-children.js';
import { FormField } from '../../forms/forms-core/components/form-field/FormField.js';
import { Label } from '../../forms/forms-core/components/label/Label.js';
import { TextInput } from '../../forms/text-input/TextInput.js';
import { Menu } from '../../navigation/menu/Menu.js';
import { Modal } from '../../overlays/modal/Modal.js';
import { Tooltip } from '../../overlays/tooltip/Tooltip.js';
import type { SimpleTableColumnDef } from '../../tables/SimpleTable/public.api.js';
import { SimpleTable } from '../../tables/SimpleTable/SimpleTable.js';
import { Flex } from '../flex/Flex.js';
import * as styles from './HelpMenu.sty.js';

const COMPONENT_NAME = 'HelpMenu';

type MenuItem = {
  label: ReactNode;
  icon: ReactNode;
  key: keyof HelpMenuProps['entries'];
};

const MENU_ITEMS: MenuItem[] = [
  {
    label: (
      <FormattedMessage defaultMessage="What's new" id="TY3F/kE6n91t0TL0" />
    ),
    icon: <WhatsNewIcon />,
    key: 'whatsNew',
  },
  {
    label: (
      <FormattedMessage defaultMessage="Get started" id="kOQNUENAg02eeaTY" />
    ),
    icon: <GuideIcon />,
    key: 'getStarted',
  },
  {
    label: (
      <FormattedMessage defaultMessage="Documentation" id="npzzIhowJGIZhj8d" />
    ),
    icon: <DescriptionIcon />,
    key: 'documentation',
  },
  {
    label: (
      <FormattedMessage
        defaultMessage="Keyboard shortcuts"
        id="JmaVR5qUnuxE3qnb"
      />
    ),
    icon: <KeyboardIcon />,
    key: 'keyboardShortcuts',
  },
  {
    label: (
      <FormattedMessage
        defaultMessage="Try in Playground"
        id="Nar807M4NJhqMw/T"
      />
    ),
    icon: <PlayIcon />,
    key: 'playground',
  },
  {
    label: (
      <FormattedMessage defaultMessage="Share feedback" id="Ia87HCOLBoxMcV9t" />
    ),
    icon: <FeedbackIcon />,
    key: 'feedback',
  },
  {
    label: (
      <FormattedMessage defaultMessage="About this app" id="9/nhbjHpPPAhMbHo" />
    ),
    icon: <InformationIcon />,
    key: 'about',
  },
] as const;

type PackageVersionRow = HelpMenuDependency & { duplicate: boolean };

const DuplicateCell = ({ value }: { value: boolean }) =>
  value ? (
    <FormattedMessage defaultMessage="Yes" id="U5fgWD8U9siN4Gse" />
  ) : (
    <FormattedMessage defaultMessage="No" id="gWxS/SvdlKY80JRI" />
  );

export const manifestQueryClient = new QueryClient({
  defaultOptions: { queries: { staleTime: Infinity, retry: 0 } },
});

const PackageVersionsModal = ({
  show,
  onDismiss,
}: {
  show: boolean;
  onDismiss: () => void;
}) => {
  const intl = useIntl();
  const [filter, setFilter] = useState('');

  const { data: dependencies = [] } = useQuery<HelpMenuDependency[]>(
    {
      queryKey: ['appManifest'],
      enabled: show,
      queryFn: async () => {
        try {
          const manifestSdk =
            await import('@dynatrace-sdk/client-app-engine-registry').catch(
              () => {
                return {
                  appEngineRegistryAppsClient: {
                    getApp: () =>
                      Promise.resolve({ manifest: { packages: [] } }),
                  },
                };
              },
            );
          const client = manifestSdk.appEngineRegistryAppsClient;

          const appInfo = await client.getApp({
            id: getAppId(),
            addFields: 'manifest',
          });

          return appInfo?.manifest?.packages ?? [];
        } catch {
          return [];
        }
      },
    },
    manifestQueryClient,
  );

  const rows = useMemo<PackageVersionRow[]>(() => {
    const counts = new Map<string, number>();
    for (const dep of dependencies) {
      counts.set(dep.name, (counts.get(dep.name) ?? 0) + 1);
    }
    return dependencies.map((dep) => ({
      ...dep,
      duplicate: (counts.get(dep.name) ?? 0) > 1,
    }));
  }, [dependencies]);

  const filteredData = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) {
      return rows;
    }
    return rows.filter((pkg) => pkg.name.toLowerCase().includes(query));
  }, [filter, rows]);

  const columns = useMemo<SimpleTableColumnDef<PackageVersionRow>[]>(
    () => [
      {
        id: 'package',
        accessor: 'name',
        header: intl.formatMessage({
          defaultMessage: 'Package',
          id: 'dDHj3yQ8W+M8/6Cy',
        }),
      },
      {
        id: 'version',
        accessor: 'version',
        header: intl.formatMessage({
          defaultMessage: 'Version',
          id: 'LaYAv5QEhDEHqVMW',
        }),
      },
      {
        id: 'duplicate',
        accessor: 'duplicate',
        header: intl.formatMessage({
          defaultMessage: 'Duplicate',
          id: 'ly1XN52z5b521OrX',
        }),
        cell: DuplicateCell,
      },
    ],
    [intl],
  );

  return (
    <Modal
      title={intl.formatMessage({
        defaultMessage: 'Built with',
        id: 'vkJWCbh6wLAsnHfT',
      })}
      show={show}
      onDismiss={onDismiss}
    >
      <Flex flexDirection="column" gap={16} width="100%">
        <FormField>
          <Label>
            <FormattedMessage
              defaultMessage="Filter Dependencies"
              id="u0KgUM/9t1gS7DB9"
            />
          </Label>
          <TextInput value={filter} onChange={setFilter} />
        </FormField>
        <SimpleTable
          data={filteredData}
          columns={columns}
          style={{ width: '100%' }}
        />
      </Flex>
    </Modal>
  );
};

const HelpMenuFooter = () => {
  const version = getAppVersion();
  if (!version) {
    return null;
  }
  return (
    <Menu.Item
      className={styles.versionItem}
      onSelect={(event) => event.preventDefault()}
    >
      <div className={styles.versionText}>
        <FormattedMessage
          defaultMessage="Version {version}"
          description="App version label"
          id="W2KEm8AIcM8pOixf"
          values={{ version }}
        />
      </div>
    </Menu.Item>
  );
};

const HelpMenuLink = ({
  href,
  target = '_blank',
  onSelect,
  label,
  icon,
  ...rest
}: {
  href: string;
  target?: string;
  onSelect?: () => void;
  label: ReactNode;
  icon?: ReactNode;
}) => {
  return (
    <Menu.Link
      {...rest}
      onClick={() => onSelect?.()}
      href={href}
      target={target}
    >
      {icon ? <Menu.Prefix>{icon}</Menu.Prefix> : null}
      <Flex justifyContent="space-between" width="inherit">
        {label}
        <ExternalLinkIcon style={{ flexShrink: 0 }} />
      </Flex>
    </Menu.Link>
  );
};

const HelpMenuButton = ({
  onSelect,
  label,
  icon,
  ...rest
}: {
  onSelect: () => void;
  label: ReactNode;
  icon?: ReactNode;
}) => {
  return (
    <Menu.Item onSelect={onSelect} {...rest}>
      {icon ? <Menu.Prefix>{icon}</Menu.Prefix> : null}
      {label}
    </Menu.Item>
  );
};

/** @public */
export type HelpMenuButtonEntry = {
  onSelect: () => void;
};

/** @public */
export type HelpMenuLinkEntry = {
  href: string;
  /** Specify how to open the linked content. */
  target?: string;
  onSelect?: () => void;
};

/**
 * Use the default behavior via app configuration / SDKs
 * @public
 **/
export type HelpMenuDefault = 'default';

/** @public */
export interface HelpMenuCustomLinkEntry extends HelpMenuLinkEntry {
  label: string;
}
/** @public */
export interface HelpMenuCustomItemEntry extends HelpMenuButtonEntry {
  label: string;
}
/** @public */
export type HelpMenuChildrenEntry = Array<
  HelpMenuCustomLinkEntry | HelpMenuCustomItemEntry
>;

/**
 * A single dependency rendered in the package versions modal.
 * @public
 */
export type HelpMenuDependency = {
  name: string;
  version: string;
};

/**
 * An entry that can either trigger a custom callback/link or fall back to the
 * platform-provided default behavior.
 * @public
 */
export type HelpMenuDefaultableEntry =
  | HelpMenuButtonEntry
  | HelpMenuLinkEntry
  | HelpMenuDefault;

/**
 * An entry that can either trigger a custom callback/link or expand into a
 * sub-menu of nested entries.
 * @public
 */
export type HelpMenuExpandableEntry =
  | HelpMenuButtonEntry
  | HelpMenuLinkEntry
  | HelpMenuChildrenEntry;

/** @public */
export interface HelpMenuProps
  extends
    WithChildren,
    AriaLabelingProps,
    StylingProps,
    DataTestId,
    MaskingProps {
  entries: {
    whatsNew?: HelpMenuDefaultableEntry;
    getStarted?: HelpMenuExpandableEntry;
    documentation?: HelpMenuExpandableEntry;
    keyboardShortcuts?: HelpMenuDefaultableEntry;
    playground?: HelpMenuDefaultableEntry;
    feedback?: HelpMenuExpandableEntry | HelpMenuDefault;
    about?: HelpMenuDefaultableEntry;
  };
  onOpenChange?: (isOpen: boolean) => void;
}

/**
 * A prebuilt menu as entrypoint for app specific help links and actions.
 * @public
 */
export const HelpMenu = (props: HelpMenuProps) => {
  const {
    entries,
    onOpenChange,
    children,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isPackageVersionsOpen, setIsPackageVersionsOpen] = useState(false);
  const intl = useIntl();

  const label = intl.formatMessage({
    defaultMessage: 'Help',
    id: 'xHrhU3DP4e0nge7c',
  });

  const handleOpenChange = (value: boolean) => {
    setIsOpen(value);
    onOpenChange?.(value);
  };

  const email = getCurrentUserDetails().email.toLowerCase();
  const showPackageVersionsOnInternalUser =
    (email.includes('@dynatrace') || email.includes('@ruxit')) &&
    email.endsWith('.com');

  return (
    <Menu
      open={isOpen}
      onOpenChange={handleOpenChange}
      data-dt-component={COMPONENT_NAME}
      data-testid={dataTestId}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
    >
      <Tooltip text={label}>
        <Menu.Trigger
          data-dt-component={COMPONENT_NAME}
          data-dt-children-name="OpenHelpMenu"
        >
          <Button aria-label={label}>
            <Button.Prefix>
              <HelpIcon />
            </Button.Prefix>
          </Button>
        </Menu.Trigger>
      </Tooltip>
      <Menu.Content
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        aria-describedby={ariaDescribedBy}
        aria-details={ariaDetails}
      >
        {MENU_ITEMS.map(({ label, key, icon }) => {
          const entry = entries[key];
          if (!entry) {
            return null;
          }

          if (entry === 'default') {
            return (
              <HelpMenuDefaults
                key={key}
                entryKey={key}
                icon={icon}
                label={label}
              />
            );
          }

          if (Array.isArray(entry)) {
            return (
              <Menu.Sub key={key}>
                <Menu.SubTrigger>
                  <Menu.Prefix>{icon}</Menu.Prefix>
                  {label}
                </Menu.SubTrigger>
                <Menu.SubContent>
                  {entry.map((entryChild) => {
                    if ('href' in entryChild) {
                      return (
                        <HelpMenuLink
                          key={entryChild.label}
                          label={entryChild.label}
                          onSelect={entryChild.onSelect}
                          href={entryChild.href}
                          target={entryChild.target}
                        />
                      );
                    }
                    return (
                      <HelpMenuButton
                        key={entryChild.label}
                        label={entryChild.label}
                        onSelect={entryChild.onSelect}
                      />
                    );
                  })}
                </Menu.SubContent>
              </Menu.Sub>
            );
          }

          if ('href' in entry) {
            return (
              <HelpMenuLink
                key={key}
                label={label}
                icon={icon}
                onSelect={entry.onSelect}
                href={entry.href}
                target={entry.target}
              />
            );
          }
          return (
            <HelpMenuButton
              key={key}
              label={label}
              icon={icon}
              onSelect={entry.onSelect}
            />
          );
        })}
        {children}
        {showPackageVersionsOnInternalUser && (
          <HelpMenuButton
            onSelect={() => setIsPackageVersionsOpen(true)}
            label={
              <FormattedMessage
                defaultMessage="Package versions"
                description="HelpMenu entry that opens the package versions modal"
                id="+1GjcNJdKJiZg6v/"
              />
            }
          />
        )}
        <HelpMenuFooter />
      </Menu.Content>
      {showPackageVersionsOnInternalUser && (
        <Suspense>
          <PackageVersionsModal
            show={isPackageVersionsOpen}
            onDismiss={() => setIsPackageVersionsOpen(false)}
          />
        </Suspense>
      )}
    </Menu>
  );
};

const HelpMenuDefaults = (props: {
  label: ReactNode;
  entryKey: keyof HelpMenuProps['entries'];
  icon: ReactNode;
}) => {
  switch (props.entryKey) {
    case 'whatsNew':
      return (
        <HelpMenuButton
          onSelect={() =>
            sendIntent(
              {
                appId: getAppId(),
                version: getAppVersion(),
              },
              {
                recommendedAppId: 'dynatrace.hub',
                recommendedIntentId: 'view_app_releases_add_on_intent',
              },
            )
          }
          label={props.label}
          icon={props.icon}
        />
      );
    case 'keyboardShortcuts':
      return (
        <HelpMenuButton
          onSelect={() => {
            // This is a workaround to call the keyboard modal for now.
            // In the future this should be called via an sdk.
            // It is not yet clear yet in which sdk this will be available.
            // If it is available this will be replaced.
            // oxlint-disable-next-line typescript/no-explicit-any
            (globalThis as any).dtRuntime?.uiCommands?.showUiCommands?.();
          }}
          label={props.label}
          icon={props.icon}
        />
      );
    case 'playground':
      return (
        <HelpMenuLink
          href={`https://wkf10640.apps.dynatrace.com/ui/apps/${getAppId()}`}
          label={props.label}
          icon={props.icon}
        />
      );
    case 'feedback':
      return (
        <HelpMenuButton
          onSelect={() => {
            sendIntent(
              {
                appId: getAppId(),
                appName: getAppName(),
                appVersion: getAppVersion(),
              },
              {
                recommendedAppId: 'dynatrace.hub',
                recommendedIntentId: 'post_feedback_add_on_intent',
              },
            );
          }}
          label={props.label}
          icon={props.icon}
        />
      );
    case 'about':
      return (
        <HelpMenuButton
          onSelect={() => {
            sendIntent(
              { appId: getAppId() },
              {
                recommendedAppId: 'dynatrace.hub',
                recommendedIntentId: 'view_app_details',
              },
            );
          }}
          label={props.label}
          icon={props.icon}
        />
      );
    default:
      return null;
  }
};
